export interface RelatedCapability {
  label: string;
  href: string;
}

export interface Insight {
  slug: string;
  title: string;
  summary: string;
  category: string;
  categories: string[];
  readingTime: string;
  icon: string;
  businessChallenge: string;
  keyConsiderations: string[];
  practicalRecommendations: string[];
  commonRisks: string[];
  actionChecklist: string[];
  relatedCapabilities: RelatedCapability[];
}

export const insightFilters = [
  "Infrastructure",
  "Database and Data Platforms",
  "Application Engineering",
  "Power Platform and SharePoint",
  "Delivery Teams",
  "Government Technology",
];

export const insights: Insight[] = [
  {
    slug: "modernizing-enterprise-infrastructure",
    title: "Modernizing Enterprise Infrastructure Without Disrupting Operations",
    summary:
      "Infrastructure modernization is as much about continuity as it is about technology. Practical planning around dependencies, maintenance windows, and phased delivery helps organizations evolve networks, servers, virtualization, and platforms while keeping operations stable.",
    category: "Infrastructure",
    categories: ["Infrastructure"],
    readingTime: "6 min read",
    icon: "ri-server-line",
    businessChallenge:
      "Many organizations operate legacy infrastructure that is expensive to maintain, difficult to secure, and slow to support new requirements. The challenge is modernizing this environment without interrupting the applications and users who depend on it every day.",
    keyConsiderations: [
      "Document the current-state environment, including network segments, servers, virtualization hosts, and platform dependencies.",
      "Map application and data dependencies before moving any workload to understand blast radius.",
      "Identify realistic maintenance windows and business-impact thresholds for each change.",
      "Define a phased approach that delivers value in increments rather than a single high-risk cutover.",
      "Prepare clear rollback plans and validation steps for every modernization milestone.",
    ],
    practicalRecommendations: [
      "Start with an accurate inventory and a shared baseline so scope, risk, and cost are understood before work begins.",
      "Prioritize modernization by business impact, moving high-value and lower-risk workloads first.",
      "Run a small pilot to validate tooling, procedures, and team readiness before broad rollout.",
      "Schedule changes around approved maintenance windows and keep operations teams informed at each phase.",
      "Maintain documentation throughout so support teams can operate the new environment confidently.",
    ],
    commonRisks: [
      "Unplanned downtime caused by incomplete dependency mapping.",
      "Data loss or corruption during migration without tested backup and recovery.",
      "Integration breakage when adjacent systems are modernized out of sequence.",
      "Scope creep from undocumented requirements discovered late in the effort.",
      "Support gaps if operations staff are not trained before handover.",
    ],
    actionChecklist: [
      "Complete a current-state infrastructure inventory.",
      "Map application, data, and integration dependencies.",
      "Define success metrics and rollback criteria for each phase.",
      "Schedule approved maintenance windows.",
      "Run a pilot modernization with documented validation.",
      "Execute phased rollout with ongoing status communication.",
      "Document the new environment and train operations teams.",
      "Conduct a post-implementation review before closing out.",
    ],
    relatedCapabilities: [
      { label: "Infrastructure & Network Engineering", href: "/capabilities/infrastructure-network-engineering" },
      { label: "Database & Data Platform Services", href: "/capabilities/database-data-platform" },
      { label: "Explore All Capabilities", href: "/capabilities" },
    ],
  },
  {
    slug: "database-administration-mission-critical",
    title: "Database Administration for Mission-Critical Environments",
    summary:
      "Oracle and SQL environments that support critical operations need more than routine maintenance. Sound database administration brings data integrity, proactive monitoring, and operational continuity to the center of the delivery plan.",
    category: "Database and Data Platforms",
    categories: ["Database and Data Platforms"],
    readingTime: "7 min read",
    icon: "ri-database-2-line",
    businessChallenge:
      "Mission-critical databases must stay available, accurate, and performant while the organization continues daily operations. Gaps in monitoring, recovery procedures, or change control can turn a routine patch or configuration change into a costly outage.",
    keyConsiderations: [
      "Establish documented backup and recovery procedures and test them on a defined schedule.",
      "Put proactive monitoring in place for availability, performance, and capacity trends.",
      "Maintain strict access controls and auditability for privileged database accounts.",
      "Follow change management so schema, configuration, and patch changes are reviewed and reversible.",
      "Plan for operational continuity, including failover and recovery scenarios.",
    ],
    practicalRecommendations: [
      "Automate routine health checks and alerting so issues surface before they become incidents.",
      "Keep recovery procedures current and rehearse them, not just document them.",
      "Tune indexes, queries, and resource allocation based on observed workload, not assumptions.",
      "Align patch and upgrade cycles with application release and maintenance windows.",
      "Maintain current schema, configuration, and runbook documentation for support teams.",
    ],
    commonRisks: [
      "Data loss when backups are untested or retention policies are unclear.",
      "Performance degradation from unmonitored growth or inefficient queries.",
      "Unplanned outages triggered by unreviewed configuration or schema changes.",
      "Security exposure from overly broad database account privileges.",
      "Recovery failure discovered only when an incident is already underway.",
    ],
    actionChecklist: [
      "Inventory all mission-critical databases and their support owners.",
      "Document and test backup and recovery procedures.",
      "Configure proactive availability and performance monitoring.",
      "Review and reduce privileged account access.",
      "Define a change management and rollback process for database changes.",
      "Establish continuity and failover scenarios.",
      "Keep runbooks and schema documentation current.",
      "Schedule regular recovery exercises with operations teams.",
    ],
    relatedCapabilities: [
      { label: "Database & Data Platform Services", href: "/capabilities/database-data-platform" },
      { label: "Infrastructure & Network Engineering", href: "/capabilities/infrastructure-network-engineering" },
      { label: "Explore All Capabilities", href: "/capabilities" },
    ],
  },
  {
    slug: "building-effective-federal-it-delivery-teams",
    title: "Building Effective Federal IT Delivery Teams",
    summary:
      "Successful federal delivery depends on the right mix of roles aligned to defined workstreams. Engineers, developers, database administrators, analysts, and subject-matter experts each support a clear part of the mission when responsibilities and communications are well defined.",
    category: "Delivery Teams",
    categories: ["Delivery Teams", "Government Technology"],
    readingTime: "6 min read",
    icon: "ri-team-line",
    businessChallenge:
      "Federal programs often need to stand up delivery capacity quickly while meeting security, compliance, and reporting requirements. Without clearly defined workstreams and role boundaries, teams can duplicate effort, miss requirements, or fail to hand work off cleanly.",
    keyConsiderations: [
      "Define workstreams with clear owners, deliverables, and acceptance criteria.",
      "Align roles such as engineers, developers, DBAs, analysts, and SMEs to specific tasks.",
      "Account for security and compliance requirements in every workstream.",
      "Establish regular status and escalation communication with the customer.",
      "Plan for knowledge transfer and continuity when team members change.",
    ],
    practicalRecommendations: [
      "Start with a staffing plan that maps each required role to a defined workstream.",
      "Assign a single accountable lead for each workstream to reduce ambiguity.",
      "Use a shared status cadence so progress, risks, and dependencies stay visible.",
      "Onboard staff with clear security and compliance training before they touch systems.",
      "Document processes and decisions to support continuity and auditability.",
    ],
    commonRisks: [
      "Undefined roles leading to overlapping or missing responsibilities.",
      "Skill gaps that surface only after work is underway.",
      "Compliance issues from staff touching systems before proper clearance and training.",
      "Poor handoffs between workstreams causing rework and delays.",
      "Single points of failure when knowledge is held by one individual.",
    ],
    actionChecklist: [
      "Define workstreams, owners, and acceptance criteria.",
      "Map required roles and staffing to each workstream.",
      "Establish security and compliance onboarding for all team members.",
      "Set a regular status and escalation cadence.",
      "Define handoff and review points between workstreams.",
      "Document processes, decisions, and operational knowledge.",
      "Put continuity and knowledge-transfer plans in place.",
    ],
    relatedCapabilities: [
      { label: "Explore All Capabilities", href: "/capabilities" },
      { label: "Application & Platform Engineering", href: "/capabilities/application-platform-engineering" },
      { label: "Database & Data Platform Services", href: "/capabilities/database-data-platform" },
    ],
  },
  {
    slug: "power-platform-sharepoint-process-improvement",
    title: "Power Platform and SharePoint for Process Improvement",
    summary:
      "Power Apps, Power Automate, Power BI, and SharePoint can streamline workflows, reporting, and collaboration when applied to well-understood processes. Success depends on clear requirements, sound governance, and user adoption as much as on the tools themselves.",
    category: "Power Platform and SharePoint",
    categories: ["Power Platform and SharePoint"],
    readingTime: "6 min read",
    icon: "ri-window-line",
    businessChallenge:
      "Teams often rely on manual, disconnected processes that are slow and error-prone. Power Platform and SharePoint offer ways to automate workflows, consolidate reporting, and improve collaboration, but without planning they can create shadow IT and maintenance burdens.",
    keyConsiderations: [
      "Start with a clear problem statement and a defined process, not with a tool selection.",
      "Confirm licensing, security, and data-handling requirements early.",
      "Use Power Apps to digitize forms and workflows, and Power Automate to handle repetitive steps.",
      "Use Power BI to consolidate reporting from reliable, governed data sources.",
      "Plan for training and user adoption so improvements are actually used.",
    ],
    practicalRecommendations: [
      "Begin with a small, high-value process to demonstrate benefit before scaling.",
      "Document the current process and the target workflow before building anything.",
      "Establish governance for environments, permissions, and app ownership.",
      "Design reports and dashboards around clear user questions and trusted data.",
      "Provide hands-on training and support channels to drive adoption.",
    ],
    commonRisks: [
      "Shadow IT from apps built outside governance and support structures.",
      "Poor data quality flowing into Power BI reports and dashboards.",
      "Integration failures when connections are not tested end to end.",
      "Low adoption when users are not trained or involved in design.",
      "Maintenance gaps when apps lack clear owners and documentation.",
    ],
    actionChecklist: [
      "Define the business problem and target workflow.",
      "Confirm licensing, security, and data requirements.",
      "Document the current process and measurable goals.",
      "Build a pilot app, flow, or report against a real need.",
      "Establish governance for environments, ownership, and access.",
      "Test integrations and data end to end.",
      "Train users and provide ongoing support.",
      "Review usage and refine based on feedback.",
    ],
    relatedCapabilities: [
      { label: "Microsoft Solutions", href: "/capabilities/microsoft-solutions" },
      { label: "Application & Platform Engineering", href: "/capabilities/application-platform-engineering" },
      { label: "Database & Data Platform Services", href: "/capabilities/database-data-platform" },
    ],
  },
  {
    slug: "planning-successful-application-integrations",
    title: "Planning Successful Application Integrations",
    summary:
      "Application integration rarely fails because of a single technical step; it fails in the details of requirements, APIs, data mapping, dependencies, testing, and handover. A structured plan keeps these moving parts aligned and reduces the risk of rework.",
    category: "Application Engineering",
    categories: ["Application Engineering"],
    readingTime: "7 min read",
    icon: "ri-node-tree",
    businessChallenge:
      "Connecting applications through APIs and data flows is complex, with many systems, owners, and data definitions involved. Without disciplined planning, integrations can deliver incorrect data, break dependent systems, or leave teams unable to support them.",
    keyConsiderations: [
      "Capture clear functional and data requirements before design begins.",
      "Understand the APIs, authentication, rate limits, and contracts of each system.",
      "Map source and target data fields and define transformation and validation rules.",
      "Identify downstream dependencies and sequence integration work accordingly.",
      "Plan comprehensive testing, user training, and a supported transition.",
    ],
    practicalRecommendations: [
      "Document the API contracts and data dictionaries for every system involved.",
      "Build a data-mapping specification agreed to by all data owners.",
      "Use phased integration with checkpoints so problems surface early.",
      "Test at unit, integration, and user-acceptance levels with realistic data.",
      "Plan a clear transition with documentation, training, and support handover.",
    ],
    commonRisks: [
      "Data mismatches from unagreed field definitions or formats.",
      "Dependency failures when systems are integrated out of sequence.",
      "Incomplete testing that misses edge cases and error handling.",
      "Training gaps that leave users unsure how to use the integrated solution.",
      "Unclear transition ownership after go-live.",
    ],
    actionChecklist: [
      "Document functional and data requirements.",
      "Review API contracts and access for all systems.",
      "Create an agreed data-mapping and transformation spec.",
      "Map dependencies and sequence integration milestones.",
      "Run unit, integration, and user-acceptance testing.",
      "Train users on the integrated workflow.",
      "Plan and execute a supported transition to operations.",
    ],
    relatedCapabilities: [
      { label: "Application & Platform Engineering", href: "/capabilities/application-platform-engineering" },
      { label: "Database & Data Platform Services", href: "/capabilities/database-data-platform" },
      { label: "Microsoft Solutions", href: "/capabilities/microsoft-solutions" },
    ],
  },
  {
    slug: "requirements-to-uat-delivery-framework",
    title: "From Requirements to UAT: A Practical Delivery Framework",
    summary:
      "Software and platform delivery benefits from a repeatable framework that moves from requirements analysis through user stories, workflow design, testing, and user acceptance. A structured approach keeps scope controlled and handover clean.",
    category: "Application Engineering",
    categories: ["Application Engineering", "Delivery Teams"],
    readingTime: "7 min read",
    icon: "ri-list-check-3",
    businessChallenge:
      "Delivery efforts often stall or rework because requirements are vague, expectations are undefined, or acceptance is left to the end. A practical framework keeps the whole team aligned from the start and makes the path to user acceptance explicit.",
    keyConsiderations: [
      "Translate business needs into clear requirements and prioritized user stories.",
      "Design workflows that reflect how work actually happens today and how it should work.",
      "Define testing expectations, including functional, integration, and edge-case coverage.",
      "Facilitate user acceptance with defined scenarios and clear sign-off criteria.",
      "Plan an operational handover with documentation, training, and support.",
    ],
    practicalRecommendations: [
      "Start with requirements analysis that separates what is needed from what is assumed.",
      "Write concise user stories with explicit acceptance criteria.",
      "Design workflows collaboratively with the people who will use them.",
      "Build a test plan early and align it to the user stories.",
      "Conduct structured UAT sessions and record findings for prioritization.",
    ],
    commonRisks: [
      "Vague requirements that lead to built-in assumptions and rework.",
      "Scope creep from undocumented changes during delivery.",
      "Untested edge cases that fail in production.",
      "Unclear acceptance criteria that delay sign-off.",
      "Incomplete handover leaving operations teams underprepared.",
    ],
    actionChecklist: [
      "Complete requirements analysis with stakeholders.",
      "Write prioritized user stories with acceptance criteria.",
      "Design and validate target workflows.",
      "Develop a test plan aligned to the user stories.",
      "Run functional and integration testing.",
      "Facilitate structured user acceptance testing.",
      "Document and execute the operational handover.",
    ],
    relatedCapabilities: [
      { label: "Application & Platform Engineering", href: "/capabilities/application-platform-engineering" },
      { label: "Database & Data Platform Services", href: "/capabilities/database-data-platform" },
      { label: "Explore All Capabilities", href: "/capabilities" },
    ],
  },
  {
    slug: "building-successful-technology-teaming-partnerships",
    title: "Building Successful Technology Teaming Partnerships",
    summary:
      "Successful teaming partnerships are built on clear responsibilities, honest communication, and compliance. When roles, staffing, delivery governance, and measurable expectations are agreed up front, both parties can collaborate with confidence.",
    category: "Delivery Teams",
    categories: ["Delivery Teams", "Government Technology"],
    readingTime: "5 min read",
    icon: "ri-contract-line",
    businessChallenge:
      "Organizations often team to combine capabilities for a pursuit or delivery effort. Without clear agreements, partners can misalign on responsibilities, compliance obligations, staffing, or expectations, leading to friction and delivery problems.",
    keyConsiderations: [
      "Define each partner's responsibilities, deliverables, and boundaries up front.",
      "Align on compliance, security, and reporting obligations for the effort.",
      "Staff each workstream with the right mix of skills and accountability.",
      "Establish delivery governance, including status, decisions, and escalation.",
      "Set measurable expectations for performance, quality, and communication.",
    ],
    practicalRecommendations: [
      "Document a clear scope of work and responsibility matrix before starting.",
      "Agree on communication cadence and decision-making authority early.",
      "Confirm each party's compliance and security obligations in writing.",
      "Assign named leads and clear escalation paths on both sides.",
      "Track progress against agreed milestones and review performance regularly.",
    ],
    commonRisks: [
      "Unclear responsibilities causing gaps or duplicated effort.",
      "Communication breakdowns when status and escalation are not defined.",
      "Compliance failures from undefined security and reporting obligations.",
      "Staffing churn that erodes continuity and institutional knowledge.",
      "Misaligned expectations that surface only during delivery.",
    ],
    actionChecklist: [
      "Define scope, responsibilities, and a responsibility matrix.",
      "Agree on compliance, security, and reporting obligations.",
      "Establish communication cadence and escalation paths.",
      "Assign named leads and delivery governance.",
      "Define measurable expectations and milestones.",
      "Track performance and review regularly.",
    ],
    relatedCapabilities: [
      { label: "Explore All Capabilities", href: "/capabilities" },
      { label: "Application & Platform Engineering", href: "/capabilities/application-platform-engineering" },
      { label: "Infrastructure & Network Engineering", href: "/capabilities/infrastructure-network-engineering" },
    ],
  },
  {
    slug: "supporting-security-aware-it-operations",
    title: "Supporting Security-Aware IT Operations",
    summary:
      "Infrastructure and database teams play a practical role in supporting security-aware operations through access controls, monitoring, and alignment with vulnerability-management processes. Simple, repeatable practices reduce exposure without disrupting the mission.",
    category: "Infrastructure",
    categories: ["Infrastructure", "Government Technology"],
    readingTime: "6 min read",
    icon: "ri-shield-check-line",
    businessChallenge:
      "Security is a shared responsibility, and infrastructure and database teams directly influence access, visibility, and patching. Gaps in these basics can expose systems, slow incident response, and complicate compliance in regulated environments.",
    keyConsiderations: [
      "Apply least-privilege access controls to systems, accounts, and data.",
      "Ensure monitoring captures the events needed to detect and investigate issues.",
      "Align operational work with the organization's vulnerability-management process.",
      "Patch and harden systems systematically within approved change windows.",
      "Document secure operating procedures so teams apply them consistently.",
    ],
    practicalRecommendations: [
      "Enforce least privilege and review privileged access on a defined schedule.",
      "Centralize monitoring and alerting so anomalies are visible to the right teams.",
      "Integrate infrastructure and database changes into the vulnerability-management process.",
      "Schedule and track patches and hardening against the approved baseline.",
      "Document and rehearse secure operating and incident-handling procedures.",
    ],
    commonRisks: [
      "Access misconfiguration from overly broad account privileges.",
      "Unmonitored events that delay detection and response.",
      "Missed patches that leave known vulnerabilities open.",
      "Insecure defaults left in place during deployment.",
      "Inconsistent procedures when documentation and training are lacking.",
    ],
    actionChecklist: [
      "Review and reduce privileged access across systems and databases.",
      "Confirm monitoring covers the events needed for detection.",
      "Align operational changes with the vulnerability-management process.",
      "Schedule patching and hardening against the approved baseline.",
      "Document secure operating procedures.",
      "Rehearse incident-handling steps with operations teams.",
    ],
    relatedCapabilities: [
      { label: "Infrastructure & Network Engineering", href: "/capabilities/infrastructure-network-engineering" },
      { label: "Database & Data Platform Services", href: "/capabilities/database-data-platform" },
      { label: "Explore All Capabilities", href: "/capabilities" },
    ],
  },
];

export const contactEmail = "governmentsales@mitajacorp.com";